import { useNavigate, useSearchParams } from "react-router-dom";
import MeetingDateSelectionSection from "./components/MeetingDateSelectionSection";
import MeetingGeneralInfoSection from "./components/MeetingGeneralInfoSection";
import ReviewMeetingPoll from "./components/ReviewMeetingPoll";
import { useRef, useState } from "react";
import {
  MeetingModeEnum,
  ReviewFormData,
  TimeSlotRange,
} from "pages/CalendarsPage/SchedulingSection/form.types";
import dayjs from "@lib/dayjs";
import { createInterview } from "endpoints/interview.service";
import { useSnackbar } from "notistack";
import LoadingAnimation from "assets/svg/LoadingAnimation";

export function availabilitySlotsForApiPayload(
  slots: Record<string, TimeSlotRange[]>
) {
  const finalSlots = [];
  Object.entries(slots).forEach(([key, timeSlots]) => {
    const date = key;
    timeSlots?.forEach((timeSlot) => {
      finalSlots.push({
        startTime: dayjs(timeSlot?.startTime)
          .set("year", dayjs(date).year())
          .set("month", dayjs(date).month())
          .set("date", dayjs(date).date())
          .toISOString(),
        endTime: dayjs(timeSlot?.endTime)
          .set("year", dayjs(date).year())
          .set("month", dayjs(date).month())
          .set("date", dayjs(date).date())
          .toISOString(),
      });
    });
  });

  return finalSlots;
}

const CreateMeetingInvitePage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const meetingGeneralInfoRef = useRef(null);
  const meetingDateSelectionRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [formData, setFormData] = useState<ReviewFormData>({});
  const [isInterviewCreating, setIsInterviewCreating] = useState(false);

  const section = searchParams.get("section");
  const isMeetingInvite = searchParams.get("type") === "invite";

  const addParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    });
  };

  const isDateSelectionSection = section === "dateSelection";
  const isReviewSection = section === "review";

  const handleNext = async () => {
    if (!section) {
      try {
        const data = await meetingGeneralInfoRef.current?.submit();
        setFormData((prev) => {
          return { ...prev, ...data };
        });
        addParam("section", "dateSelection");
      } catch (error) {
        console.log("Form invalid", error);
      }
    } else if (isDateSelectionSection) {
      try {
        const data = await meetingDateSelectionRef.current?.submit();
        setFormData((prev) => {
          return { ...prev, ...data };
        });
        addParam("section", "review");
      } catch (error) {
        console.log("Form invalid", error);
      }
    } else if (isReviewSection) {
      try {
        setIsInterviewCreating(true);
        const apiPayload = {
          candidateId: formData?.candidateDetails?.candidateId,
          jobId: formData?.jobDetails?.jobId,
          meetingTitle: formData?.meetingTitle,
          meetingDescription: formData?.meetingDescription,
          meetingDuration: formData?.meetingDuration?.duration,
          meetingMode: MeetingModeEnum[formData?.meetingMode],
          slotSelectionType: isMeetingInvite ? "specific" : "general",
        };
        if (isMeetingInvite) {
          apiPayload["specificSlot"] = availabilitySlotsForApiPayload(
            formData?.availabilitySlots
          )[0];
        } else {
          apiPayload["generalAvailabilitySlots"] =
            availabilitySlotsForApiPayload(formData?.availabilitySlots);
        }
        if (formData?.meetingMode === MeetingModeEnum["Virtual Meeting"])
          apiPayload["url"] = formData?.url;
        console.log("api payload", apiPayload);
        return;
        const { success } = await createInterview(apiPayload);
        if (success) {
          enqueueSnackbar(
            `${isMeetingInvite ? "Invite" : "Poll"} sent successfully.`,
            {
              variant: "success",
            }
          );
          navigate("/calendar/meeting");
        }
        console.log("success data", success);
        // const apiPayload = await reviewMeetingPollRef.current?.submit();
        // console.log("api payload", apiPayload, formData);
      } catch (error) {
        console.log("Error creating meeting", error);
      } finally {
        setIsInterviewCreating(false);
      }
    }
  };

  // useEffect(() => {
  //   if (
  //     (isDateSelectionSection || isReviewSection) &&
  //     isObjectEmpty(formData)
  //   ) {
  //     navigate("/calendar/create-meeting?type=invite");
  //   }
  // }, [formData, isDateSelectionSection, isReviewSection]);

  return (
    <div
      className={`flex flex-col rounded-lg w-full p-3 items-center h-[calc(100vh-130px)]`}
    >
      <div
        className={`${
          isDateSelectionSection ? "w-full" : "w-[75%]"
        } flex flex-col flex-1 min-h-0`}
      >
        <div className="flex items-center justify-between w-full flex-shrink-0">
          <button
            onClick={() => navigate(-1)}
            className="bg-light-black rounded-md text-white font-semibold text-sm py-2 px-6"
          >
            Back
          </button>
          <span className="text-primary font-semibold text-lg">
            {isReviewSection ? "Review" : "Create"}{" "}
            {isMeetingInvite ? "Invite" : "Meeting Poll"}
          </span>
          <button
            onClick={handleNext}
            className={`bg-primary rounded-md text-white font-semibold text-sm py-2 ${
              isInterviewCreating ? "pl-6" : "px-6"
            } flex items-center gap-4`}
          >
            {isReviewSection
              ? isMeetingInvite
                ? "Send Invite"
                : "Send Poll"
              : "Next"}
            {isInterviewCreating ? <LoadingAnimation /> : null}
          </button>
        </div>

        {/* Main components */}
        {isReviewSection ? (
          <ReviewMeetingPoll
            formData={formData}
            isMeetingInvite={isMeetingInvite}
          />
        ) : !isDateSelectionSection ? (
          <MeetingGeneralInfoSection
            ref={meetingGeneralInfoRef}
            formData={formData}
          />
        ) : (
          <MeetingDateSelectionSection
            ref={meetingDateSelectionRef}
            formData={formData}
          />
        )}
      </div>
    </div>
  );
};

export default CreateMeetingInvitePage;
